/**
 * <s-welcome-popup> — first-visit email offer with a reopen tab
 * Opens its <dialog> after `delay` ms, or sooner on desktop when the pointer leaves through the top of the window.
 * Once closed, a small "20% off" tab ([data-popup-tab]) stays on screen so the offer can be reopened any time; the
 * popup itself comes back on its own after `snooze-days`. Signing up (the `joined` attribute, set by the server after a
 * successful form post) stops both for good. Add ?welcome to any URL to force it open.
 *
 * Attributes:
 *   delay           — milliseconds before the automatic open. Default 8000.
 *   storage-key     — localStorage key prefix.
 *   snooze-days     — days before the popup reopens by itself after being closed. Default 7.
 *   open-on-connect — open right away (server-returned success or errors).
 *   joined          — the visitor just signed up.
 */
class SWelcomePopup extends HTMLElement {
  #dialog;
  #tab;
  #ac;
  #timer;
  #key = 'hm-welcome-popup';

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;
    this.#tab = this.querySelector('[data-popup-tab]');
    this.#key = this.getAttribute('storage-key') || this.#key;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    if (this.hasAttribute('joined')) this.#set(`${this.#key}-joined`, '1');

    this.#dialog.addEventListener('close', () => {
      this.#set(this.#key, String(Date.now()));
      this.#showTab();
    }, opts);
    this.#dialog.addEventListener('click', (e) => {
      if (e.target === this.#dialog) this.#dialog.close();
    }, opts);

    this.querySelector('[data-popup-tab-open]')?.addEventListener('click', () => this.#open(true), opts);
    this.querySelector('[data-popup-tab-dismiss]')?.addEventListener('click', () => {
      this.#hideTab();
      try { sessionStorage.setItem(`${this.#key}-tab-off`, '1'); } catch {}
    }, opts);

    this.querySelector('[data-copy-code]')?.addEventListener('click', (e) => this.#copy(e.currentTarget), opts);

    const forced = new URLSearchParams(window.location.search).has('welcome');
    if (this.hasAttribute('open-on-connect') || forced) {
      this.#open(true);
      return;
    }

    if (this.#joined()) return;

    if (this.#snoozed()) {
      this.#showTab();
      return;
    }

    // Show the "20% off" tab from the first moment of the visit, not only after the popup has been closed.
    this.#showTab();

    const delay = Number(this.getAttribute('delay')) || 8000;
    this.#timer = setTimeout(() => this.#open(), delay);

    // Desktop exit intent: the pointer leaves through the top of the window (toward tabs or the address bar).
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      const armAt = Date.now() + 3000;
      document.documentElement.addEventListener('mouseleave', (e) => {
        if (e.clientY <= 0 && Date.now() > armAt) this.#open();
      }, opts);
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#timer);
  }

  #open(fromVisitor = false) {
    clearTimeout(this.#timer);
    if (this.#dialog.open) return;
    if (!fromVisitor && document.querySelector('dialog[open]')) return;
    this.#hideTab();
    this.#dialog.showModal();
  }

  #showTab() {
    if (!this.#tab || this.#joined()) return;
    try { if (sessionStorage.getItem(`${this.#key}-tab-off`)) return; } catch {}
    this.#tab.hidden = false;
  }

  #hideTab() {
    if (this.#tab) this.#tab.hidden = true;
  }

  #joined() {
    try { return localStorage.getItem(`${this.#key}-joined`) === '1'; } catch { return false; }
  }

  #snoozed() {
    try {
      const closedAt = Number(localStorage.getItem(this.#key));
      // Older visits stored "1"; treat those as expired so returning visitors see the offer again.
      if (!closedAt || closedAt < 1000000000000) return false;
      const days = Number(this.getAttribute('snooze-days')) || 7;
      return Date.now() - closedAt < days * 86400000;
    } catch {
      return false;
    }
  }

  async #copy(button) {
    const label = button.querySelector('[data-copy-label]');
    try {
      await navigator.clipboard.writeText(button.dataset.copyCode || '');
      if (label?.dataset.copiedText) label.textContent = label.dataset.copiedText;
    } catch {
      // Clipboard can be blocked; the code is still visible to type in.
    }
  }

  #set(key, value) {
    try { localStorage.setItem(key, value); } catch {}
  }
}

if (!customElements.get('s-welcome-popup')) customElements.define('s-welcome-popup', SWelcomePopup);
